/**
 * ApplyReadinessCard
 * ---------------------------------------------------------------
 * Progress readout that answers the "How do I move on to Apply?"
 * question the moment a student shortlists their first course.
 *
 * Mobile-first stacked card: count + status ("Apply is unlocked" /
 * "N courses to unlock Apply"), full-width explanation, a 4-segment
 * progress row below the threshold, and a "Go to Apply →" CTA once ready.
 *
 * Reads /api/shortlist for the source of truth. Also accepts an
 * optional `count` prop so pages that already track the shortlist
 * (like the Prove page) can pass their local Set-size in and avoid
 * a duplicate round-trip.
 *
 * The threshold is exported so /compare's ConfirmPillarPanel can
 * pull the same value — one place to change, no drift.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const APPLY_MIN_COURSES = 4;

export default function ApplyReadinessCard({
  count = null,          // pre-computed count from parent (avoids /shortlist fetch)
  compact = false,       // slimmer variant for inline placement (e.g. DegreeDetail)
  className = "",
  testId = "apply-readiness-card",
  hideWhenAnonymous = true,
}) {
  const { user } = useAuth();
  const [fetched, setFetched] = useState(null);

  useEffect(() => {
    if (count !== null) return;             // parent controls the count
    if (!user) { setFetched(0); return; }   // anonymous → 0
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/shortlist");
        if (!cancelled) setFetched((data.items || []).length);
      } catch {
        if (!cancelled) setFetched(0);
      }
    })();
    return () => { cancelled = true; };
  }, [user, count]);

  // If the parent didn't pass a count and we're anonymous, hide by
  // default — anon users can't shortlist and the message is noise.
  if (count === null && fetched === null) return null;
  const have = count !== null ? count : (fetched || 0);
  if (hideWhenAnonymous && !user && have === 0) return null;

  const need = APPLY_MIN_COURSES;
  const ready = have >= need;
  const left = Math.max(need - have, 0);
  const plural = (n) => n + (n === 1 ? " course" : " courses");
  const status = ready ? "Apply is unlocked" : (have > 0 ? left + " more " + (left === 1 ? "course" : "courses") : plural(left)) + " to unlock Apply";
  const tally = ready ? plural(have) + " shortlisted" : have + " of " + need + " shortlisted";

  // Mobile-first: stacked rows, full-width prose. From sm up the CTA/progress sit to the right of the text.
  return (
    <section
      data-testid={testId}
      aria-label="Apply readiness"
      className={[
        "rounded-2xl border text-left grid gap-x-6 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center",
        compact ? "gap-y-2 px-4 py-3" : "gap-y-3 px-5 py-4 sm:px-6 sm:py-5",
        ready ? "border-emerald-200 bg-emerald-50" : "border-stone-200 bg-white",
        className,
      ].join(" ")}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span
            aria-hidden="true"
            className={[
              "inline-flex shrink-0 items-center justify-center rounded-full font-mono font-semibold",
              compact ? "h-7 min-w-[1.75rem] px-1.5 text-xs" : "h-9 min-w-[2.25rem] px-2 text-sm",
              ready ? "bg-emerald-600 text-white" : have > 0 ? "bg-[#4C1D95] text-white" : "bg-stone-200 text-stone-700",
            ].join(" ")}
          >
            {have}
          </span>
          <div className="min-w-0">
            <p className={["font-semibold leading-snug", compact ? "text-sm" : "text-base", ready ? "text-emerald-900" : "text-stone-900"].join(" ")}
              data-testid={`${testId}-status`}>
              {status}
            </p>
            <p className="text-xs text-stone-500" data-testid={`${testId}-count`}>{tally}</p>
          </div>
        </div>
        <p className={["mt-2 max-w-prose leading-relaxed text-stone-700", compact ? "text-xs" : "text-sm"].join(" ")}
          data-testid={`${testId}-text`}>
          {ready
            ? <>You can head to <strong>Apply</strong> and confirm your final list.</>
            : have === 0
              ? <>Shortlist <strong>{need}</strong> courses to move on to the <strong>Apply</strong> stage.</>
              : <>Shortlist <strong>{left}</strong> more {left === 1 ? "course" : "courses"} to move on to the <strong>Apply</strong> stage — you need {need} in total.</>}
        </p>
      </div>
      {!ready && (
        <div
          role="progressbar" aria-valuemin={0} aria-valuemax={need} aria-valuenow={have}
          aria-label={have + " of " + need + " courses shortlisted"}
          className="flex w-full max-w-[14rem] items-center gap-1.5 sm:w-48"
          data-testid={`${testId}-progress`}
        >
          {Array.from({ length: need }).map((_, i) => (
            <span key={i} className={["h-2 flex-1 rounded-full transition-colors", i < have ? "bg-emerald-500" : "bg-stone-200"].join(" ")} />
          ))}
        </div>
      )}
      {ready && (
        <Link
          to="/applications"
          data-testid={`${testId}-cta`}
          className={[
            "inline-flex min-h-11 w-full items-center justify-center gap-1 whitespace-nowrap rounded-full bg-emerald-600 px-5 font-medium text-white transition-colors hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700 focus-visible:ring-offset-2 sm:w-auto",
            compact ? "text-xs" : "text-sm",
          ].join(" ")}
        >
          Go to Apply <span aria-hidden="true">→</span>
        </Link>
      )}
    </section>
  );
}
